import { CLICKED_EXPORT_DOCUMENT_IN_CANCEL_MODAL } from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ReactNode } from "react";
import { useExportFolderMutation } from "store/apis/documents";
import { SubscriptionInProfile } from "types/Models/subscription";
import { openDownloadUrl } from "util/openDownloadUrl";
import { CancelSubscriptionConfirmation } from "./CancelSubscriptionConfirmation";
import {
  CurrentSubscriptionCard,
  FreePlanCard,
} from "./CurrentSubscriptionCard";
import { Button } from "./DesignSystem/Button/Button";
import Modal from "./DesignSystem/Modal/Modal";
import { SmallTriangleWarning } from "./icons/SmallTriangleWarning";
import { ThinArrowLine } from "./icons/ThinArrowLine";
import { SubscriptionAccessLossPoints } from "./SubscriptionAccessLossPoints";

export const PlatformSubscriptionCancelModal = ({
  isOpen,
  onClose,
  AccessPoints,
  planCancelLabel,
  dataExportable,
  handleCancelSubscription,
  subscription,
  isCurrentYearIncorporated,
  groupSubscriptionId,
}: {
  isOpen: boolean;
  onClose: () => void;
  AccessPoints: Array<string | ReactNode>;
  planCancelLabel: string;
  dataExportable?: boolean;
  handleCancelSubscription: () => void;
  subscription: SubscriptionInProfile;
  isCurrentYearIncorporated?: boolean;
  groupSubscriptionId: string;
}) => {
  const [exportFolder, { reset }] = useExportFolderMutation();
  const group = useCurrentGroupContext();
  const currentGroupId = group.uuid;
  const {
    alertToast,
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();
  const { trackEvent } = useAnalytics();
  const cancelConfirmationModal = useModal();

  const exportFolderCallback = async () => {
    if (currentGroupId) {
      const loadingtoastId = loadingToast({
        title: "Preparing Export",
        message: "This may take upto one minute",
      });
      try {
        trackEvent(CLICKED_EXPORT_DOCUMENT_IN_CANCEL_MODAL);

        const { download_url } = await exportFolder({
          groupId: currentGroupId,
        }).unwrap();
        successToast({
          title: "Export Completed",
        });
        openDownloadUrl(download_url);
        reset();
      } catch (error: any) {
        alertToast({ message: error?.data?.error?.message });
      } finally {
        dismissToast(loadingtoastId);
      }
    }
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Modal.Content size="regular">
          <Modal.Header>
            <Modal.Title>Cancel Subscription</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <div className="t-text-body-sm t-text-text-60">
              {planCancelLabel}
            </div>
            <div className="t-flex t-items-center t-justify-between t-gap-5 t-mt-4">
              <CurrentSubscriptionCard
                planName={subscription.subscription_name}
                amount={subscription.amount}
                frequency={subscription.billing_cycle}
                isCurrentYearIncorporated={isCurrentYearIncorporated}
              />
              <span className="t-text-neutral-30">
                <ThinArrowLine />
              </span>
              <FreePlanCard />
            </div>
            <SubscriptionAccessLossPoints points={AccessPoints} />
            {dataExportable && (
              <div className="t-flex t-gap-2.5 t-p-2.5 t-border t-border-solid t-border-orange-20 t-bg-orange-0 t-rounded-lg t-mt-4">
                <span className="t-text-orange-80">
                  <SmallTriangleWarning />
                </span>
                <div className="t-space-y-2">
                  <div className="t-text-body-sm t-text-text-30">
                    If you continue, your account will be permanently deleted
                    after 30 days. We recommend you to export all your data
                    before continuing.
                  </div>
                  <Button onClick={exportFolderCallback} size="small">
                    Export all data
                  </Button>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button>Stay on current plan</Button>
            </Modal.RawClose>
            <Button onClick={cancelConfirmationModal.open} customType="primary">
              Continue
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
      {cancelConfirmationModal.isOpen && (
        <CancelSubscriptionConfirmation
          subscriptionId={groupSubscriptionId}
          {...cancelConfirmationModal}
          handleCancelSubscription={handleCancelSubscription}
        />
      )}
    </>
  );
};
